<template>
  <div>
    <div class="block-header">
      <bread-crumb></bread-crumb>
    </div>
    <div class="ReportPageComponent">
      <div>
        <div class="actions-row">
          <h5 class="header-title">Patient Reports</h5>
          <button
            class="export-all-button"
            @click="exportAllXlsx"
            :disabled="!hasAnyXlsx"
          >
            Export All (.xlsx)
          </button>
        </div>
        <div v-if="flattenedReports.length" class="report-list">
          <div
            v-for="report in flattenedReports"
            :key="report.reportId"
            class="report-item"
          >
            <div class="report-info">
              <img
                src="../../../assets/username.png"
                alt="User Icon"
                class="icon"
              />
              <span class="identifier"
                >{{ report.patient_identifier || "N/A"}}</span
              >
              <span class="sex" style="margin-right: 50px"
                >Sex: {{ report.sex }}</span
              >
              <span class="sex" style="margin-right: 50px"
                >Format: {{ report.format }}</span
              >
              <img
                src="../../../assets/calendar.png"
                alt="Calendar Icon"
                class="icon"
              />
              <span class="created-at">Created At: {{ report.createdAt }}</span>
            </div>
            <button @click="downloadReport(report)" class="download-button">
              Download
            </button>
          </div>
        </div>
        <p v-else>Loading patient reports...</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import BreadCrumb from "@/components/BreadCrumb";
import ExcelJS from "exceljs";

export default {
  name: "PatientReports",
  components: {
    BreadCrumb,
  },
  setup() {
    const patients = ref([]);
    const flattenedReports = ref([]);
    const loading = ref(true);
    const errors = ref([]);
    const hasAnyXlsx = computed(() =>
      flattenedReports.value.some(
        (r) => (r.format || "").toUpperCase() === "XLSX"
      )
    );

    // fetch patient data
    const fetchPatients = async () => {
      const fasdBackendURL =
        localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
      const url = `${fasdBackendURL}/api/patients`;
      const token = localStorage.getItem("userJWtoken");

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch patients: ${response.statusText}`);
        }

        const data = await response.json();
        patients.value = data.map((patient) => ({
          id: patient.id,
          name: patient.name,
          surname: patient.surname,
          patient_identifier: patient.patient_identifier,
          sex: patient.sex,
          reports: [],
        }));
      } catch (error) {
        console.error("Error fetching patients:", error);
        errors.value.push(error.message);
        patients.value = [];
      }
    };

    const fetchReportsForPatients = async () => {
      const fasdBackendURL =
        localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
      const url = `${fasdBackendURL}/api/fasdReports/underPatientID`;
      const token = localStorage.getItem("userJWtoken");

      for (const patient of patients.value) {
        try {
          const response = await fetch(`${url}/${patient.id}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            patient.reports = data.map((report) => ({
              id: report.id,
              createdAt: report.created_at,
              format: report.format,
            }));
          } else if (response.status === 404) {
            console.warn(`No reports found for patient ID: ${patient.id}`);
          } else {
            throw new Error(
              `Failed to fetch reports for patient ID ${patient.id}: ${response.statusText}`
            );
          }
        } catch (error) {
          console.error(
            `Error fetching reports for patient ID ${patient.id}:`,
            error
          );
          errors.value.push(`Patient ID ${patient.id}: ${error.message}`);
        }
      }

      flattenedReports.value = patients.value.flatMap((patient) =>
        patient.reports.map((report) => ({
          patientId: patient.id,
          name: patient.name,
          surname: patient.surname,
          sex: patient.sex,
          patient_identifier: patient.patient_identifier,
          reportId: report.id,
          createdAt: new Date(report.createdAt).toLocaleDateString(),
          format: report.format,
        }))
      );
    };

    const downloadReport = async (report) => {
      try {
        const fasdBackendURL =
          localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
        const token = localStorage.getItem("userJWtoken");

        const res = await fetch(
          `${fasdBackendURL}/api/fasdReports/${report.reportId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const blob = await res.blob();

        // Decide extension
        const ct = (res.headers.get("Content-Type") || "").toLowerCase();
        const ext =
          (report.format || "").toUpperCase() === "XLSX" ||
          ct.includes("spreadsheetml")
            ? "xlsx"
            : "pdf";

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Report_${report.patient_identifier}.${ext}`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Download error:", err);
        alert("Failed to download report.");
      }
    };
    const exportAllXlsx = async () => {
      try {
        const fasdBackendURL =
          localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
        const token = localStorage.getItem("userJWtoken");

        const xlsxReports = flattenedReports.value.filter(
          (r) => (r.format || "").toUpperCase() === "XLSX"
        );
        if (!xlsxReports.length) return alert("No XLSX reports to export.");

        const combined = new ExcelJS.Workbook();
        const out = combined.addWorksheet("AllReports");
        let headerAdded = false;

        for (const r of xlsxReports) {
          const res = await fetch(
            `${fasdBackendURL}/api/fasdReports/${r.reportId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          if (!res.ok) continue;

          const wb = new ExcelJS.Workbook();
          await wb.xlsx.load(await res.arrayBuffer());
          const ws = wb.worksheets[0];
          if (!ws) continue;

          for (let i = 1; i <= ws.rowCount; i++) {
            const srcRow = ws.getRow(i);
            const values = srcRow.values.slice(1);

            if (i === 1) {
              // header row
              if (!headerAdded) {
                out.addRow(values);
                headerAdded = true;
              }
            } else {
              out.addRow(values);
            }
          }
        }

        const buffer = await combined.xlsx.writeBuffer();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(
          new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );
        a.download = "All_Patient_Reports.xlsx";
        a.click();
      } catch (e) {
        console.error("Export failed:", e);
        alert("Failed to export XLSX data.");
      }
    };

    onMounted(async () => {
      await fetchPatients();
      await fetchReportsForPatients();
      loading.value = false;
    });

    return {
      patients,
      flattenedReports,
      loading,
      errors,
      downloadReport,
      hasAnyXlsx,
      exportAllXlsx,
    };
  },
};
</script>
<style scoped>
.ReportPageComponent {
  background-color: #f7f7f7;
  padding: 20px;
  border-radius: 8px;
  margin-left: 290px;
  margin-right: 38px;
  min-height: fit-content;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.header-title {
  color: #555c65;
  font-weight: bold;
  margin-bottom: 20px;
  text-align: left;
}

.report-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.report-item {
  background-color: #fff;
  padding: 10px 15px;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.report-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 12px rgba(0, 0, 255, 0.3);
}

.report-info {
  display: flex;
  align-items: center;
  gap: 3px;
}

.icon {
  width: 12px;
  height: 12px;
}

.identifier {
  font-size: 15px;
  margin-right: 20px;
  width: 140px;
}
.sex {
  width: 140px;
}

.created-at {
  font-size: 14px;
  color: #555;
}

.download-button {
  margin-left: 10px;
  padding: 5px 10px;
  background-color: #004ead;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.download-button:hover {
  background-color: #003678;
}
.actions-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.export-all-button {
  padding: 15px 15px;
  background-color: #2bc551;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  outline: none;
}
.export-all-button:hover {
  background-color: #003678;
}

.export-all-button:focus {
  outline: none;
  box-shadow: none;
}
.export-all-button:disabled {
  background-color: #2bc55275;
  cursor: not-allowed;
}
</style>
